'use client';

import { useState } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { logos } from '@/shared/assets/images';
import { COMPANY } from '@voley/shared-types/constants';
import { BRAND_BLUE } from '@voley/shared-types/constants';

export default function GetStartedPage() {
  const brandBlue = BRAND_BLUE;
  const [hoveredOption, setHoveredOption] = useState<'owner' | 'team' | null>(null);

  return (
    <div
      data-testid="onboarding-get-started-page"
      className="min-h-screen flex flex-col items-center justify-center py-12 px-4 sm:px-6 lg:px-8"
      style={{ backgroundColor: '#ffffff' }}
    >
      {/* Subtle gradient background */}
      <div
        className="fixed inset-0 pointer-events-none"
        style={{
          background: 'radial-gradient(ellipse at top right, rgba(53, 129, 207, 0.04) 0%, transparent 50%), radial-gradient(ellipse at bottom left, rgba(53, 129, 207, 0.03) 0%, transparent 50%)',
        }}
      />

      {/* Main content container */}
      <div className="w-full max-w-md bg-white rounded-2xl shadow-sm border border-gray-100 relative z-10 p-5 sm:p-8">
        {/* Logo */}
        <div className="flex justify-center mb-6">
          <Link href="/home">
            {/* Mobile logo: BallOnly */}
            <Image
              src={logos.ballOnly}
              alt="Voley"
              width={52}
              height={52}
              className="h-12 w-12 md:hidden"
              priority
            />
            {/* Desktop logo: wordmark */}
            <Image
              src={logos.voleyT}
              alt="Voley"
              width={120}
              height={28}
              className="hidden md:inline h-8 w-auto"
              priority
            />
          </Link>
        </div>

        {/* Header */}
        <div className="mb-8 text-center">
          <h2
            className="text-2xl font-semibold tracking-tight"
            style={{ color: '#111827' }}
          >
            Get started with Voley
          </h2>
          <p
            className="mt-2 text-base"
            style={{ color: '#6B7280' }}
          >
            Choose the option that best describes you
          </p>
        </div>

        {/* Options */}
        <div className="space-y-4">
          {/* Agency Owner Option */}
          <Link
            href="/signup"
            className="block w-full p-5 rounded-xl transition-all duration-200"
            style={{
              border: hoveredOption === 'owner' ? `2px solid ${brandBlue}` : '2px solid #E5E7EB',
              backgroundColor: hoveredOption === 'owner' ? `${brandBlue}05` : '#ffffff',
              boxShadow: hoveredOption === 'owner' ? '0 4px 6px -1px rgb(0 0 0 / 0.1)' : 'none'
            }}
            onMouseEnter={() => setHoveredOption('owner')}
            onMouseLeave={() => setHoveredOption(null)}
          >
            <div className="flex items-start">
              <div className="flex-shrink-0 mt-0.5">
                <div
                  className="flex items-center justify-center h-10 w-10 rounded-xl text-white"
                  style={{ backgroundColor: brandBlue }}
                >
                  <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M4 4a2 2 0 012-2h8a2 2 0 012 2v12a1 1 0 110 2h-3a1 1 0 01-1-1v-2a1 1 0 00-1-1H9a1 1 0 00-1 1v2a1 1 0 01-1 1H4a1 1 0 110-2V4zm3 1h2v2H7V5zm2 4H7v2h2V9zm2-4h2v2h-2V5zm2 4h-2v2h2V9z" clipRule="evenodd" />
                  </svg>
                </div>
              </div>
              <div className="ml-4">
                <h3
                  className="text-base font-semibold mb-1"
                  style={{ color: '#111827' }}
                >
                  I&apos;m an agency owner
                </h3>
                <p className="text-sm" style={{ color: '#6B7280' }}>
                  Create a new agency account and start your free trial
                </p>
              </div>
            </div>
          </Link>

          {/* Team Member Option */}
          <Link
            href="/join-agency"
            className="block w-full p-5 rounded-xl transition-all duration-200"
            style={{
              border: hoveredOption === 'team' ? `2px solid ${brandBlue}` : '2px solid #E5E7EB',
              backgroundColor: hoveredOption === 'team' ? `${brandBlue}05` : '#ffffff',
              boxShadow: hoveredOption === 'team' ? '0 4px 6px -1px rgb(0 0 0 / 0.1)' : 'none'
            }}
            onMouseEnter={() => setHoveredOption('team')}
            onMouseLeave={() => setHoveredOption(null)}
          >
            <div className="flex items-start">
              <div className="flex-shrink-0 mt-0.5">
                <div
                  className="flex items-center justify-center h-10 w-10 rounded-xl text-white"
                  style={{ backgroundColor: brandBlue }}
                >
                  <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v3h8v-3z" />
                  </svg>
                </div>
              </div>
              <div className="ml-4">
                <h3
                  className="text-base font-semibold mb-1"
                  style={{ color: '#111827' }}
                >
                  I&apos;m joining my agency
                </h3>
                <p className="text-sm" style={{ color: '#6B7280' }}>
                  My agency already uses Voley and I need to create an account
                </p>
              </div>
            </div>
          </Link>
        </div>

        {/* Sign in link */}
        <div className="mt-8 text-center">
          <p className="text-sm" style={{ color: '#6B7280' }}>
            Already have an account?{' '}
            <Link
              href="/login"
              className="font-medium transition-colors hover:underline"
              style={{ color: brandBlue }}
            >
              Sign in
            </Link>
          </p>
        </div>

        {/* Back to home link */}
        <div className="mt-6 text-center">
          <Link
            href="/home"
            className="inline-flex items-center gap-1.5 text-sm transition-colors hover:underline"
            style={{ color: '#9CA3AF' }}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Back to Home
          </Link>
        </div>
      </div>

      {/* Footer */}
      <div className="mt-8 text-center relative z-10">
        <p className="text-sm text-gray-400">
          {COMPANY.copyrightNotice}
        </p>
      </div>
    </div>
  );
}
